/**
 * The one way a write leaves the browser for Olive's API.
 *
 * A write that meets an expired sign-in must not follow the platform's 302
 * into the sign-in page: the browser rejects that cross-origin hop as a
 * TypeError, indistinguishable from an outage. So every POST, PUT, PATCH
 * and DELETE is sent with redirect 'manual'; the 302 then arrives as an
 * 'opaqueredirect' response, and this helper throws the tagged auth_expired
 * ApiError every error sentence reads as "Your sign-in ran out".
 *
 * Whether the page then goes to sign-in is the caller's: `redirectToSignIn`
 * only where the page's work is kept (src/lib/keptWork.ts registers it, or
 * the notes keep their own autosave) or where the write already went to
 * sign-in before writeFetch. Anywhere else (a record edit dialog, a chosen
 * file) the person's work is still on screen; leaving it is theirs to
 * choose, through the banner's Sign in door. Either way the write is
 * recorded for the Errors screen, without its content.
 *
 * tools/lint-write-fetch.sh keeps every write in src/ on this helper.
 */

import { authExpiredError, authExpiry } from './api';
import { keepAllUnsavedWork } from './keptWork';

export interface WriteFetchOptions {
  /** The page's work is kept: an expired sign-in goes to sign-in. */
  redirectToSignIn?: boolean;
}

export async function writeFetch(url: string, init: RequestInit, opts: WriteFetchOptions = {}): Promise<Response> {
  const res = await fetch(url, { ...init, redirect: 'manual' });
  if (res.type === 'opaqueredirect') {
    const label = `${init.method ?? 'POST'} ${url}`;
    if (opts.redirectToSignIn === true) {
      authExpiry.redirectToLogin(label);
      throw authExpiredError(label, true);
    }
    keepAllUnsavedWork(label);
    throw authExpiredError(label, false);
  }
  return res;
}
